import * as angular from "angular";
import {IController, ITimeoutService} from "angular";
import * as _ from "lodash";
import {MapsCommonService} from "../../services/mapsCommon.service";
import {PlanningAlertsService} from "../../../planning/services/planning-alerts.service";
import {MapUtilsService} from "../../../commons/angular/services/map-utils.service";
import {TaskModel} from "../../../tasks/models/task.model";
import {RouteTaskModel} from "../../../routes/models/route-task.model";
import {RoutesService} from "../../../routes/services/routes-service.service";

export class RoutesMapComponent implements IController {
  routes: any;
  serviceAreas: any;
  selectedServiceIds: string[];
  hiddenRouteIds: string[];
  onTaskMove: Function;
  onTaskSelect: Function;
  ungroupedRouteIds: Set<string>;

  private mapRef: any;
  private siteLocation: any;
  private filters = {
    traffic: false,
    routes: true,
    serviceAreas: true
  };
  private map: any;
  private mapPolygons: any = [];
  private isReady: boolean;
  private routesMarkersLayer: any;
  private hoveredRoutePolyline: any = null;

  private _dragMoveListener: google.maps.MapsEventListener | null = null;
  private _dragFeature: google.maps.Data.Feature | null = null;
  private _initialMarkerPosition: any;
  private _isDragging = false;
  private _selectedKeys: { [key: string]: boolean };
  private _listeners: google.maps.MapsEventListener[] = [];

  constructor(private mapsCommonService: MapsCommonService,
              private $timeout: ITimeoutService,
              private planningAlertsService: PlanningAlertsService,
              private tasksConstants,
              private mapUtilsService: MapUtilsService,
              private routesService: RoutesService) { }

  $onInit() {
    this.siteLocation = this.mapsCommonService.getSiteLocation();
    this.map = {
      isReady: false,
      center: this.siteLocation,
      zoom: 12,
      events: {
        idle: (map) => {
          if (!this.isReady) {
            this.isReady = true;
            this.mapRef = map;
            google.maps.event.trigger(this.mapRef, 'resize');
            this.centerMap();
            // resizeService.resize();
          }
        }
      },
      options: {
        styles: this.mapsCommonService.getDefaultStyle(),
        scaleControl: true,
        mapTypeControl: false
      },
      bounds: {},
      routesMarkersLayerEnabled: true,
      routesMarkersLayerCallback: (layer) => {
        this.routesMarkersLayer = layer;

        this._listeners.push(this.routesMarkersLayer.addListener("click", (event) => {
          const feature = event.feature;
          const id = feature.getProperty('id') as string;
          const serviceIds = this._getServiceIds(feature);
          const isSelected = !this._selectedKeys[id];
          let selectedIds;

          if (isSelected) {
            selectedIds = _.uniq([...this.selectedServiceIds].concat(serviceIds));
          } else {
            selectedIds = this.selectedServiceIds.filter(id => !serviceIds.includes(id));
          }

          this.onTaskSelect({ ids: selectedIds });
        }));

        this._listeners.push(this.routesMarkersLayer.addListener("mousedown", (event) => {
          const feature = event.feature;

          if (feature && feature.getGeometry().getType() === "Point") {
            this.map.options.draggable = false;
            this._initialMarkerPosition = feature.getGeometry()?.get() as google.maps.LatLng;
            this._dragFeature = feature;

            if (this._dragMoveListener) {
              google.maps.event.removeListener(this._dragMoveListener);
            }

            this._dragMoveListener = this.mapRef.addListener("mousemove", (moveEvent) => {
              this._isDragging = true;

              if (this._dragFeature) {
                this._dragFeature.setGeometry(
                  new google.maps.Data.Point(moveEvent.latLng)
                );
                this.mapUtilsService.autoPanIfNearEdge(moveEvent.pixel, this.mapRef);

                const hoveredPolyline = this.mapUtilsService.getClosestPolyline(this.routesMarkersLayer, moveEvent.latLng);

                if (hoveredPolyline) {
                  this._highlightRoutePolyline(hoveredPolyline);
                } else {
                  this._unhighlightRoutePolyline();
                }
              }
            });
          }
        }));

        this._listeners.push(this.routesMarkersLayer.addListener("mouseup", (event) => {
          const feature = event.feature;

          this.map.options.draggable = true;

          if (!this._isDragging || !feature || feature.getGeometry().getType() !== "Point") {
            this._cancelDrag();
            return;
          }

          if (!this.hoveredRoutePolyline) {
            this._cancelDrag();
            return;
          }

          const taskId: string = feature.getProperty("id");
          const newIndex: number = this.mapUtilsService.getInsertionIndex(event.latLng, this.hoveredRoutePolyline.getGeometry().getArray()) + 1;
          const fromRouteId = feature.getProperty("routeId");
          const fromRoute = this.routes.find((route) => route.id === fromRouteId);
          const toRouteId = this.hoveredRoutePolyline.getProperty("routeId");

          if (!fromRoute) {
            this._cancelDrag();
            return;
          }

          const fromRouteIds: string[] = (fromRoute.taskList || fromRoute.routeTaskList || []).map(task => task.id);
          let toRoute;
          let toRouteIds: string[];

          if (fromRouteId !== toRouteId) {
            toRoute = this.routes.find((route) => route.id === toRouteId);
            toRouteIds = (toRoute.taskList || toRoute.routeTaskList || []).map(task => task.id);
          } else {
            toRoute = fromRoute;
            toRouteIds = fromRouteIds;
          }

          if (fromRoute.locked || toRoute.locked) {
            this.planningAlertsService.routeIsLocked();
            this._cancelDrag();
            return;
          }

          const draggedTask = fromRoute.taskList?.find(task => task.id === taskId) ||
            fromRoute.routeTaskList?.find(task => task.id === taskId);

          if (!draggedTask) {
            this._cancelDrag();
            return;
          }

          const movedTasks = toRoute.taskList || toRoute.routeTaskList || [];
          const connectedIndexes = [];

          movedTasks.forEach((t, i) => {
            const serviceIds = t.bundleServiceIdList || [t.serviceId];
            const itemServiceIds = draggedTask.bundleServiceIdList || [draggedTask.serviceId];

            if ((draggedTask.id !== t.id) && !!_.intersection(serviceIds, itemServiceIds).length) {
              connectedIndexes.push(i);
            }
          });

          const currentIndex = fromRouteIds.indexOf(taskId);
          const adjustedNewIndex = fromRouteId === toRouteId && currentIndex < newIndex ? newIndex - 1 : newIndex;

          if (currentIndex === -1) {
            this._cancelDrag();
            return;
          }

          if (connectedIndexes.length) {
            if (draggedTask.taskSpec?.purpose === this.tasksConstants.typesToTitle.Pickup && connectedIndexes.some(i => i <= adjustedNewIndex)) {
              this._cancelDrag();
              this.planningAlertsService.invalidTaskOrder();
              return;
            }

            if (draggedTask.taskSpec?.purpose === this.tasksConstants.typesToTitle.Dropoff && connectedIndexes.some(i => i >= adjustedNewIndex)) {
              this._cancelDrag();
              this.planningAlertsService.invalidTaskOrder();
              return;
            }
          }

          fromRouteIds.splice(currentIndex, 1);
          toRouteIds.splice(adjustedNewIndex, 0, taskId);

          if (fromRoute.taskList) {
            const tasks = fromRoute.taskList.filter(task => taskId === task.id);
            const routeSkills = toRoute.requiredSkills;
            const tasksSkills = _.flatMap(tasks, (j) => {
              return j.serviceInfo.requiredSkills || [];
            });

            if (routeSkills && !!_.difference(tasksSkills, routeSkills).length) {
              this.planningAlertsService.noMatchingSkillsInfo();
              this._cancelDrag();
              return;
            }
          }

          this.onTaskMove({
            serviceIds: this._getServiceIds(feature),
            fromRouteId: fromRouteId,
            toRouteId: toRouteId,
            fromOrderedIds: fromRouteIds,
            toOrderedIds: toRouteIds
          });

          this._cancelDrag();
          return;
        }));
      }
    };

    if (!this.hiddenRouteIds) {
      this.hiddenRouteIds = [];
    }

    if (_.get(this.serviceAreas, 'length')) {
      this.mapPolygons = this.mapsCommonService.buildPolygons(this.serviceAreas, this.filters.serviceAreas);
    }

    this._setSelectedKeys();
  }

  $onChanges(changes) {
    // Routes Changed
    if (changes.routes && !changes.routes.isFirstChange()) {

      const currentRoutes = changes.routes.currentValue,
        previousRoutes = changes.routes.previousValue;

      const routesToAdd = _.differenceBy(currentRoutes, previousRoutes, 'id');
      const routesToRemove = _.differenceBy(previousRoutes, currentRoutes, 'id');
      const routesToUpdate = _.filter(currentRoutes, (r) => {
        const routeIndexToUpdate = _.findIndex(previousRoutes, (pr: any) => {
          return (pr.id === r.id) && (r.updatedAt > pr.updatedAt);
        });

        return routeIndexToUpdate > -1;
      })

      const allRoutesToAdd = _.concat(routesToAdd, routesToUpdate);
      const allRoutesToRemove = _.concat(routesToRemove, routesToUpdate);

      this.removeTasksFromDataLayer(_.map(allRoutesToRemove, 'id'));
      this._setSelectedKeys();

      if (allRoutesToAdd && allRoutesToAdd.length) {
        const doCenterMap = !_.get(previousRoutes, 'length');
        this.setRoutesTaskMarkers(allRoutesToAdd, doCenterMap);
      }
      this.centerMap();
    }

    if (changes.serviceAreas && !changes.serviceAreas.isFirstChange()) {
      this.mapPolygons = this.mapsCommonService.buildPolygons(this.serviceAreas, this.filters.serviceAreas);
    }

    if (changes.selectedServiceIds && !changes.selectedServiceIds.isFirstChange()) {
      this._setSelectedKeys();
      this.setTaskMarkersLayerStyle();
    }

    if (changes.hiddenRouteIds && !changes.hiddenRouteIds.isFirstChange()) {
      // Set feature style by its properties
      this.setTaskMarkersLayerVisibility();
      this.$timeout(0).then(() => {
        this.centerMap();
      });
    }

    if (changes.ungroupedRouteIds && !changes.ungroupedRouteIds.isFirstChange()) {
      const idsToUpdate = changes.ungroupedRouteIds.currentValue.symmetricDifference(changes.ungroupedRouteIds.previousValue);
      const routesToUpdate = this.routes.filter(route => idsToUpdate.has(route.id));

      this.removeTasksFromDataLayer(Array.from(idsToUpdate));
      this.setRoutesTaskMarkers(routesToUpdate, false);
    }
  }

  $onDestroy() {
    this._listeners.forEach(listener => google.maps.event.removeListener(listener));
    this._listeners = [];

    if (this._dragMoveListener) {
      google.maps.event.removeListener(this._dragMoveListener);
      this._dragMoveListener = null;
    }
  }

  centerMap() {
    const markers = this.getAllMarkers();
    this.mapsCommonService.centerMapByBounds(this.map, markers);
  }

  onToggleServiceAreas() {
    _.forEach(this.mapPolygons, (mapPolygon) => {
      mapPolygon.visible = this.filters.serviceAreas;
    });
  }

  onToggleRoutes() {
    this.setTaskMarkersLayerVisibility();
  }


  // ******************************
  //        Internal methods
  // ******************************

  private getAllMarkers() {
    let markers = [];
    if (this.routesMarkersLayer) {
      this.routesMarkersLayer.forEach((feature) => {
        const address = feature.getProperty('address');
        const routeId = feature.getProperty('routeId');

        if (!_.includes(this.hiddenRouteIds, routeId) && address && address.location) {
          const fakeMarker = new google.maps.MVCObject();
          fakeMarker.set('coords', {'latitude': address.location.lat, 'longitude': address.location.lng});
          markers.push(fakeMarker);
        }
      });
    }
    return markers;
  }

  private setRoutesTaskMarkers(routes, doCenterMap) {
    if (this.routesMarkersLayer) {
      _.forEach(routes, (r) => {
        const isGrouped: boolean = !this.ungroupedRouteIds?.has(r.id);
        const geoJson = this.mapsCommonService.convertRoutedTasksToGeoJson(this.routesService.getTaskList(r.routeTaskList, isGrouped), r, _.get(r, 'agentId'), false, !isGrouped);
        this.routesMarkersLayer.addGeoJson(geoJson);
      })

      // Set feature style by its properties
      this.setTaskMarkersLayerStyle();

      if (doCenterMap) {
        this.centerMap();
      }
    }
  }

  private setTaskMarkersLayerStyle() {
    if (this.routesMarkersLayer) {
      this.routesMarkersLayer.setStyle((feature) => {
        const updatedVisible = !_.includes(this.hiddenRouteIds, feature.getProperty('routeId'));

        return this.mapsCommonService.getRouteTaskFeatureStyle(feature, updatedVisible, this._isFeatureSelected(feature));
      });
    }
  }

  private setTaskMarkersLayerVisibility() {
    if (this.routesMarkersLayer) {
      this.routesMarkersLayer.forEach((feature) => {
        this.routesMarkersLayer.overrideStyle(feature, {
          visible: this.filters.routes && !_.includes(this.hiddenRouteIds, feature.getProperty('routeId'))
        });
      });
    }
  }

  private removeTasksFromDataLayer(routeIdsToRemove) {
    if (this.routesMarkersLayer) {
      this.routesMarkersLayer.forEach((feature) => {
        const featureRouteId = feature.getProperty('routeId');
        if (_.includes(routeIdsToRemove, featureRouteId)) {
          this.routesMarkersLayer.remove(feature);
        }
      });
    }
  }

  private _getServiceIds(feature: google.maps.Data.Feature): string[] {
    return (feature.getProperty('serviceId') ? [feature.getProperty('serviceId') as string] : feature.getProperty('serviceIds') as string[]) || [];
  }

  private _setSelectedKeys() {
    this._selectedKeys = {};
    this.routes.forEach(route => {
      const isGrouped: boolean = !this.ungroupedRouteIds?.has(route.id);

      this.selectedServiceIds?.forEach((id) => {
        const tasks = this.routesService.getTaskList(route.routeTaskList, isGrouped).filter((t) => {
          return t.bundleServiceIdList ? _.includes(t.bundleServiceIdList, id) : (t.serviceId === id);
        });

        tasks.forEach((a) => {
          this._selectedKeys[a.id] = true;
        });
      });
    });
  }

  private _isFeatureSelected(feature: google.maps.Data.Feature): boolean {
    const taskId: string = feature.getProperty('id') as string;
    return taskId ? this._selectedKeys[taskId] : false;
  }

  private _highlightRoutePolyline(polyline: any) {
    if (this.hoveredRoutePolyline === polyline) {
      return; // Already highlighted
    }

    // Unhighlight previous
    this._unhighlightRoutePolyline();
    this.hoveredRoutePolyline = polyline;

    // Get the route's color from the feature
    const routeColor = polyline.getProperty('color') || '#FFD700';

    // Override style for highlighted polyline - use route color with enhanced visibility
    this.routesMarkersLayer.overrideStyle(polyline, {
      strokeColor: routeColor,
      strokeWeight: 6,
      strokeOpacity: 1,
      zIndex: 1000
    });
  }

  private _unhighlightRoutePolyline() {
    if (this.hoveredRoutePolyline) {
      // Reset to original style
      this.routesMarkersLayer.revertStyle(this.hoveredRoutePolyline);
      this.hoveredRoutePolyline = null;
    }
  }

  private _cancelDrag() {
    this._setMarkerToInitialPosition();

    if (this._dragMoveListener) {
      google.maps.event.removeListener(this._dragMoveListener);
      this._dragMoveListener = null;
    }

    this._unhighlightRoutePolyline();

    this._isDragging = false;
    this._dragFeature = null;
    this.$timeout(() => { }, 0);
  }

  private _setMarkerToInitialPosition() {
    if (this._dragFeature && this._initialMarkerPosition) {
      this._dragFeature.setGeometry(
        new google.maps.Data.Point(this._initialMarkerPosition)
      );
    }
  }
}

angular
  .module('bringoz.admin.map')
  .component('routesMap', {
    bindings: {
      routes: '<', //[{id,tasks}]
      serviceAreas: '<',
      ungroupedRouteIds: '<?',
      selectedServiceIds: '<?',
      hiddenRouteIds: '<?',
      onTaskMove: '&',
      onTaskSelect: '&',
    },
    template: require('./routes-map.html').default,
    controller: RoutesMapComponent
  });
